<template>
    <div class="ReportViewPage">
        <DashboardSectionHeader :title="formattedTitle">
            <div v-if="exportPending" class="exporting">
                <Loading/>
                <span>Exporting...</span>
            </div>
            <Dropdown v-else-if="report && resultsCount > 0">
                <template #trigger><Icon name="download"/> Download</template>
                <template #menu>
                    <button :disabled="reportPending" @click="handlePrintPreview">Export to PDF</button>
                    <button v-if="type !== 'hauling'" :disabled="reportPending" @click="handleDownloadCsv">Export to Excel</button>
                </template>
            </Dropdown>

            <Button
                class="primary"
                :disabled="exportPending || reportPending"
                :loading="exportPending || reportPending"
                @click="handleGeneratePreview"
            >
                Preview
            </Button>
        </DashboardSectionHeader>

        <Card class="header">
            <div class="headerRow">
                <FormDateInput label="Start" v-model="startAt"/>
                <FormDateInput label="End" v-model="endAt"/>
                <FormNumber v-if="ticketFilter" v-model="ticket" label="Ticket #"/>
                <FormSelectTimeZone v-model="timeZone"/>
            </div>

            <div class="headerRow">
                <FormSelectClient v-if="clientFilter" v-model="client" nullable/>
                <FormSelectProject v-if="projectFilter" v-model="project" nullable/>
                <FormSelectDriver v-if="driverFilter" v-model="driver" nullable/>
                <FormSelectVehicle v-if="truckFilter" v-model="truck" label="Truck" type="trucks" nullable/>
                <FormSelectVehicle v-if="trailerFilter" v-model="trailer" label="Trailer" type="trailers" nullable/>
                <div v-if="['hauling', 'detailedHauling'].includes(type)" class="toggleRow">
                    <h4>Include Photos</h4>
                    <FormToggle v-model="includePhotos"/>
                </div>
            </div>
        </Card>

        <div class="reportPreview">
            <div class="reportPreviewHeader">
                <span>Tickets: {{ resultsCount }}</span>
                <span v-if="includePhotos && type === 'hauling'">Load photos: {{ loadPhotosCount }}</span>
            </div>

            <!-- Need to keep iframe rendered to mount event listener -->
            <iframe
                v-show="report"
                ref="reportFrame"
                src="/company/report"
                frameborder="0"
                @load="loadIframeStyles"
            />
            <div v-show="!report" class="reportFallback">
                <Loading v-if="reportPending"/>
            </div>
        </div>
    </div>
</template>

<script>
import { pickBy } from 'lodash';
import { mapGetters } from 'vuex';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import * as api from '@/api';
import DashboardSectionHeader from '@/components/DashboardSectionHeader';
import Dropdown from '@/components/Dropdown';
import Loading from '@/components/Loading';
import Card from '@/components/Card';
import Report from './components/Report';

export default {
    name: 'ReportViewPage',

    components: { DashboardSectionHeader, Dropdown, Loading, Card, Report },

    data: () => ({
        type: null,

        timeZone: null,
        startAt: startOfDay(subDays(new Date(), 1)).toISOString(),
        endAt: endOfDay(subDays(new Date(), 1)).toISOString(),
        client: null,
        project: null,
        driver: null,
        truck: null,
        trailer: null,
        ticket: null,
        includePhotos: false,

        previewUrl: null,
        reportUrl: null,

        reportPending: false,
        exportPending: false,

        report: null,
    }),

    computed: {
        ...mapGetters('company', ['isClarkLlc']),

        clientFilter() {
            return ['projects', 'hauling', 'tickets', 'materials', 'detailedHauling'].includes(this.type);
        },
        projectFilter() {
            return ['projects', 'hauling', 'tickets', 'materials', 'detailedHauling'].includes(this.type);
        },
        driverFilter() {
            return ['timesheets', 'hauling', 'tickets', 'materials', 'detailedHauling'].includes(this.type);
        },
        truckFilter() {
            return ['hauling', 'tickets', 'materials', 'detailedHauling'].includes(this.type);
        },
        trailerFilter() {
            return ['hauling', 'tickets', 'detailedHauling'].includes(this.type);
        },
        ticketFilter() {
            return ['hauling', 'tickets', 'detailedHauling'].includes(this.type);
        },

        resultsCount() {
            return this.report?.results ?? 0;
        },

        loadPhotosCount() {
            return this.report?.tickets?.reduce((acc1, ticket) => {
                const photoCount = ticket?.loads?.reduce((acc2, load) => load.photo ? acc2 + 1 : acc2, 0);
                return acc1 + photoCount;
            }, 0);
        },
        formattedTitle() {
            return this.type && this.type.replace(/([a-z])([A-Z])/g, '$1 $2') + ' Report'; // Add space before capital letters
        }
    },

    mounted() {
        this.type = this.$router.currentRoute.query.type;
        this.timeZone = this.$store.getters['auth/company'].timeZone ?? 'America/Phoenix';

        this.ticket = this.$router.currentRoute.query.ticket ?? null;
        if (this.ticket) {
            this.startAt = null;
            this.endAt = null;
        }
    },

    beforeDestroy() {
        window.URL.revokeObjectURL(this.previewUrl);
        window.URL.revokeObjectURL(this.reportUrl);
    },

    methods: {
        async handleGeneratePreview() {
            const report = await this.fetchReportBlob();
            if (!report) return;

            this.report = report;
            this.$refs.reportFrame.contentWindow.postMessage({
                report,
                type: this.type,
                includePhotos: this.includePhotos,
                resultsCount: this.resultsCount,
                timeZone: this.timeZone,
                // HACK: This is a temporary solution but passing it through
                // the event data since vuex is not available in the iframe
                isClarkLlc: this.isClarkLlc,
            });
        },

        async handleDownloadCsv() {
            this.exportPending = true;
            const blob = await this.fetchReportBlob('csv');
            this.downloadBlob(blob, 'csv');
            this.exportPending = false;
        },

        async fetchReportBlob(ext) {
            const query = pickBy({
                timeZone: this.timeZone,
                startAt: this.startAt,
                endAt: this.endAt,
                client: this.client,
                project: this.project,
                user: this.driver,
                truck: this.truck,
                trailer: this.trailer,
                ticketNumber: this.ticket,
                ext, // csv, pdf, or null
                company: this.$store.state.company.id,
                includePhotos: this.includePhotos,
            }, value => value !== null);

            const queryString = (new URLSearchParams(query)).toString();

            this.reportPending = true;
            const res = await api.get(`/reports/${this.type}?${queryString}`);
            this.reportPending = false;

            if (res.error) {
                return this.$awn.alert('Failed to generate report');
            }

            if (ext === 'pdf' || ext === 'csv') {
                try {
                    const type = ext === 'pdf' ? 'application/pdf' : 'text/csv';
                    return new Blob([res.body], { type });
                } catch (error) {
                    this.$awn.alert('Failed to generate report pdf url');
                    return null;
                }
            }

            return res.body;
        },

        downloadBlob(blob, ext) {
            window.URL.revokeObjectURL(this.reportUrl);
            this.reportUrl = null;

            this.reportUrl = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = this.reportUrl;
            a.download = `${new Date().toISOString().slice(0, 10)}-${this.type}.${ext}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        },

        handlePrintPreview() {
            this.$refs.reportFrame?.contentWindow?.print();
        },

        loadIframeStyles() {
            const iframeDocument = this.$refs.reportFrame?.contentWindow?.document;
            if (!iframeDocument) {
                return;
            }

            let styleElement = iframeDocument.getElementById('report-style');
            if (!styleElement) {
                styleElement = iframeDocument.createElement('style');
                styleElement.id = 'report-style';
                iframeDocument.head.appendChild(styleElement);
            }

            const pageSize = this.type === 'hauling' ? 'portrait' : 'landscape';
            styleElement.textContent = `@page { size: ${pageSize}; }`;

            this.handleGeneratePreview();
        },
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';

.ReportViewPage {
    .header {
        display: flex;
        flex-direction: column;
        padding: $admin-gutter;

        .headerRow {
            display: flex;

            &:not(:last-child) {
                margin-bottom: $grid-gutter-width;
            }

            .FormInputContainer {
                margin-bottom: 0;
            }

            & > div {
                flex: 1;
                margin-bottom: 0;
                max-width: 205px;

                &:not(:last-child) {
                    margin-right: $grid-gutter-width;
                }
            }
        }
    }

    .exporting {
        display: flex;
        align-items: center;

        .Loading {
            margin-right: $grid-gutter-width;
        }
    }

    .toggleRow {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    .DashboardSectionHeader button:disabled {
        pointer-events: none;
        opacity: 0.25;
    }

    .reportPreview {
        height: 800px;
        margin-top: $admin-gutter;
        background-color: $bg-color-1;

        iframe {
            display: block;
            width: 100%;
            height: 100%;
            border: none;
        }
    }

    .reportFallback {
        @include aspect-ratio(12, 8.5);
        background-color: $bg-color-4;

        .Loading {
            @include absolute-center(x);
            top: 60px;
        }
    }

    .reportPreviewHeader {
        padding: 5px $admin-gutter;
        background: $brand-primary;
        color: #fff;

        span + span {
            &:before {
                content: ' | ';
                margin: 0 15px;
                color: $text-color-muted;
            }
        }
    }
}
</style>
